import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EDiscountTypes, IState } from "store/store.interface";
import { getShipmentServices } from "store/actions/shipmentServices.actions";
import { IShipmentServices } from "store/reducers/shipmentServices.reducers";
import { KeyboardArrowDown } from "@material-ui/icons";
import { Box } from "common";
import MuiButton from "@material-ui/core/Button";
import ShipmentItems from "./ShipmentItems";
import { StyledFormHelperText } from "common/Input/Input.style";

interface IProps {
  label: string;
  name: string;
  setError?: (error: string) => void;
}

const NEW_TO_SHIPMENT_TYPE = "New to Shipment Type";

const MultiSelectShipmentTypes: FC<IProps> = ({ label, name, setError }) => {
  const [isMultiShipmentSelectionOpen, setIsMultiShipmentSelectionOpen] = useState(false);
  const dispatch = useDispatch();

  const { selectOptions = {}, creationForm, shipmentServices = [], selectedShipments = [] } = useSelector((state: IState) => ({
    ...state.config,
    ...state.promotion,
    ...state.shipmentServices
  }));

  const option = useMemo(() => {
    const options = [
      ...shipmentServices.map(
        ({ displayName, name, sellerType }: IShipmentServices) => ({
          label: displayName || "",
          value: name || "",
          sellerType,
        }),
      ),
    ];

    if (creationForm?.discount?.type === EDiscountTypes.FREE_DELIVERY) {
      return options.filter((res: any) => res.value !== "DEFAULT");
    }
    return options;
  }, [selectOptions, creationForm?.discount?.type, shipmentServices]);

  const handleMultiSelectShipmentClick = () => {
    setIsMultiShipmentSelectionOpen(!isMultiShipmentSelectionOpen);
  };

  const shipmentSelectionError = useMemo(() => {
    const isNewToShipment =
      creationForm?.restrictions?.isCustomerFirstOrder === NEW_TO_SHIPMENT_TYPE;

    if (!isNewToShipment) {
      return "";
    }

    if (selectedShipments.length === 0) {
      return "At least one shipment type must be selected for campaigns configured as “New to Shipment.”";
    }

    if (selectedShipments.length > 1) {
      return "Only one shipment type can be selected for campaigns configured as “New to Shipment.”";
    }

    return "";
  }, [
    creationForm?.restrictions?.isCustomerFirstOrder,
    selectedShipments.length,
  ]);

  useEffect(() => {
    setError?.(shipmentSelectionError);
  }, [shipmentSelectionError]);

  useEffect(() => {
    dispatch(getShipmentServices());
  }, []);

  return (
    <Box
      width="70%"
      mb={isMultiShipmentSelectionOpen ? '450px' : 0}
      style={{ position: 'relative' }}
    >
      <MuiButton
        color="primary"
        size="large"
        onClick={handleMultiSelectShipmentClick}
        variant="outlined"
      >
        {label} <KeyboardArrowDown />
      </MuiButton>
      <ShipmentItems
        data-testid="shipment-items"
        options={option}
        isMenuOpen={isMultiShipmentSelectionOpen}
        menuOpenHandler={setIsMultiShipmentSelectionOpen}
        name={name}
      />
      {shipmentSelectionError && (
        <StyledFormHelperText variant="filled" id={shipmentSelectionError} error={true}>
          {shipmentSelectionError}
        </StyledFormHelperText>
      )}
    </Box>
  );
};

export default MultiSelectShipmentTypes;
